import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {AppThunk} from "../store.ts";
import {ChannelService} from "../services/channel-service.ts";
import {AxiosError} from "axios";
import {
    AvailableChannelsType,
    FilteredChannelParams,
    IChannelResponseData,
    ICreateChannelParams,
    IEditChannelParams, IPrecheckChannelData
} from "../models/ChannelResponse.ts";
import {
    getChannelMode,
    removeCurrentChannelId,
    setChannelFilters,
    setChannelMode,
    setCurrentChannelId,
    setEngagementChannelFilters,
    setParamsBlackListChannels,
    setParamsFavouritesChannels,
    setParamsOfAllChannels,
    successToastify
} from "../../utils/Utils.ts";
import {IUserBlackListParams} from "../models/UserResponse.ts";
import {t} from 'i18next';
import {showCustomErrorToast} from "../../utils/customErrorToast.tsx";

type SetAllChannelsDataPayload = {
    data: AvailableChannelsType;
    append?: boolean;
};
type  InitialStateType = {
    data: IChannelResponseData[]
    publicationsChannels: IChannelResponseData[]
    filteredChannels: IChannelResponseData[]
    uiState: {
        mode: "engagementTraffic" | "transferTraffic" | string
    }
    availableChannels: AvailableChannelsType
    engagementAvailableChannels: AvailableChannelsType
    myChannels: IChannelResponseData[]
    allChannels: AvailableChannelsType
    paramsOFAllChannels: FilteredChannelParams
    blackList: AvailableChannelsType
    favourites: AvailableChannelsType
    newChannel: IChannelResponseData | null
    currentChannel: {
        data: IChannelResponseData | null
        isLoading: boolean
    },
    currentPublicationChannel: {
        data: IChannelResponseData | null
        isLoading: boolean
    }
    isLoading: boolean
    precheckChannelData: IPrecheckChannelData | null
};

const initialState: InitialStateType = {
    data: [],
    publicationsChannels: [],
    filteredChannels: [],
    uiState: {
        mode: getChannelMode() || "transferTraffic"
    },
    availableChannels: {
        data: [],
        total: 1,
        page: 1,
        limit: 1,
        pages: 1,
    },
    engagementAvailableChannels: {
        data: [],
        total: 1,
        page: 1,
        limit: 1,
        pages: 1,
    },
    myChannels: [],
    blackList: {
        data: [],
        total: 1,
        page: 1,
        limit: 1,
        pages: 1,
    },
    favourites: {
        data: [],
        total: 1,
        page: 1,
        limit: 1,
        pages: 1,
    },
    allChannels: {
        data: [],
        total: 1,
        page: 1,
        limit: 1,
        pages: 1,
    },
    paramsOFAllChannels: {},
    newChannel: null,
    currentChannel: {
        data: null,
        isLoading: false
    },
    currentPublicationChannel: {
        data: null,
        isLoading: false
    },
    isLoading: true,
    precheckChannelData: null,
}

const slice = createSlice({
    name: "channels",
    initialState: initialState,
    reducers: {
        setInitialData: (state: InitialStateType, action: PayloadAction<IChannelResponseData[]>) => {
            state.data = action.payload
        },

        setPrecheckChannelDataData: (state: InitialStateType, action: PayloadAction<IPrecheckChannelData | null>) => {
            state.precheckChannelData = action.payload
        },
        setPublicationsChannelsData: (state: InitialStateType, action: PayloadAction<IChannelResponseData[]>) => {
            state.publicationsChannels = action.payload
        },
        setAvailableChannelsData: (state: InitialStateType, action: PayloadAction<AvailableChannelsType>) => {
            const stored = localStorage.getItem('selectedChannels');
            const selectedFromStorage: any[] = stored ? JSON.parse(stored) : [];

            // Обнуляем isChecked у новых каналов
            const freshChannels = action.payload.data.map(channel => ({
                ...channel,
                isChecked: false,
            }));

            // Добавляем isChecked: true к сохранённым каналам
            const selectedChannels = selectedFromStorage.map(channel => ({
                ...channel,
                isChecked: true,
            }));

            // Удаляем дубликаты: если канал уже есть в selectedChannels, не добавляем его второй раз
            const mergedChannels = [
                ...selectedChannels,
                ...freshChannels.filter(fc => !selectedChannels.find(sc => sc.id === fc.id))
            ];

            state.availableChannels = {
                ...action.payload,
                data: mergedChannels
            };
        },
        setEngagementAvailableChannelsData: (state: InitialStateType, action: PayloadAction<AvailableChannelsType>) => {
            let selectedFromStorage: IChannelResponseData[] = [];

            try {
                const raw = localStorage.getItem('selectedEngagementChannels');
                const parsed = raw ? JSON.parse(raw) : [];
                if (Array.isArray(parsed)) {
                    selectedFromStorage = parsed;
                }
            } catch (error) {
                console.warn('Invalid JSON in localStorage.selectedEngagementChannels', error);
                selectedFromStorage = [];
            }

            const freshChannels: IChannelResponseData[] = action.payload.data.map(channel => ({
                ...channel,
                isChecked: false
            }));

            // Если ничего не выбрано в localStorage — просто сохраняем свежие данные
            if (!selectedFromStorage.length) {
                state.engagementAvailableChannels = {
                    ...action.payload,
                    data: freshChannels
                };
                return;
            }

            // Если есть сохранённые выбранные каналы, проверяем их ID и проставляем isChecked
            const updatedData: IChannelResponseData[] = freshChannels.map(channel => {
                const match = selectedFromStorage.find(
                    stored => stored.telegram_channel_id === channel.telegram_channel_id
                );
                return {
                    ...channel,
                    isChecked: Boolean(match)
                };
            });

            state.engagementAvailableChannels = {
                ...action.payload,
                data: updatedData
            };
        },
        setMyChannels: (state: InitialStateType, action: PayloadAction<IChannelResponseData[]>) => {
            state.myChannels = action.payload.map(channel => ({
                ...channel,
                isChecked: false,
            }));
        },
        toggleMyChannelChecked(state, action: PayloadAction<string>) {
            const channel = state.myChannels.find(c => c.id === action.payload);
            if (channel) {
                channel.isChecked = !channel.isChecked;
            }
        },
        setAllChannelsData: (state: InitialStateType, action: PayloadAction<SetAllChannelsDataPayload>) => {
            const {data, append} = action.payload;

            if (append && state.allChannels) {
                state.allChannels.data = [
                    ...state.allChannels.data,
                    ...data.data,
                ];
                state.allChannels.total = data.total;
                state.allChannels.page = data.page;
                state.allChannels.limit = data.limit;
                state.allChannels.pages = data.pages;
            } else {
                state.allChannels = data;
            }
        },
        setParamsOfAllChannelsData: (state: InitialStateType, action: PayloadAction<FilteredChannelParams>) => {
            state.paramsOFAllChannels = action.payload
            setParamsOfAllChannels(action.payload)
        },
        toggleChannelChecked: (state, action: PayloadAction<{ id: string }>) => {
            const channel = state.availableChannels.data.find(c => c.id === action.payload.id);
            if (channel) {
                channel.isChecked = !channel.isChecked;

                // Получаем текущее состояние localStorage
                const stored = localStorage.getItem('selectedChannels');
                const storedParsed: any[] = stored ? JSON.parse(stored) : [];

                if (channel.isChecked) {
                    // Добавляем или обновляем
                    const updated = [
                        ...storedParsed.filter(item => item.id !== channel.id),
                        {...channel, selectedAt: new Date().toISOString()}
                    ];
                    localStorage.setItem('selectedChannels', JSON.stringify(updated));
                } else {
                    // Удаляем по id
                    const updated = storedParsed.filter(item => item.id !== channel.id);
                    localStorage.setItem('selectedChannels', JSON.stringify(updated));
                }
            }
        },
        //do logic in engagement page
        toggleEngagementChannelChecked: (state, action: PayloadAction<{ id: string }>) => {
            const channel = state.engagementAvailableChannels.data.find(c => c.id === action.payload.id);
            if (!channel) return;

            channel.isChecked = !channel.isChecked;

            let storedParsed: any[] = [];

            try {
                const stored = localStorage.getItem('selectedEngagementChannels');
                const parsed = stored ? JSON.parse(stored) : [];
                if (Array.isArray(parsed)) {
                    storedParsed = parsed;
                }
            } catch (e) {
                console.warn('Invalid localStorage data in selectedEngagementChannels:', e);
            }

            if (channel.isChecked) {
                // Добавляем или обновляем
                const updated = [
                    ...storedParsed.filter(item => item.id !== channel.id),
                    {...channel, selectedAt: new Date().toISOString()}
                ];
                localStorage.setItem('selectedEngagementChannels', JSON.stringify(updated));
            } else {
                // Удаляем по id
                const updated = storedParsed.filter(item => item.id !== channel.id);
                localStorage.setItem('selectedEngagementChannels', JSON.stringify(updated));
            }
        },
        setNewChannel: (state: InitialStateType, action: PayloadAction<IChannelResponseData>) => {
            state.newChannel = action.payload
        },
        setCurrentChannel: (state: InitialStateType, action: PayloadAction<IChannelResponseData | null>) => {
            state.currentChannel.data = action.payload
        },
        setCurrentPublicationChannel: (state: InitialStateType, action: PayloadAction<IChannelResponseData>) => {
            state.currentPublicationChannel.data = action.payload
        },
        setIsLoadingCurrentChannel: (state: InitialStateType, action: PayloadAction<boolean>) => {
            state.currentChannel.isLoading = action.payload
        },
        setIsLoadingPublicationCurrentChannel: (state: InitialStateType, action: PayloadAction<boolean>) => {
            state.currentPublicationChannel.isLoading = action.payload
        },
        changeUiMode: (state: InitialStateType, action: PayloadAction<"engagementTraffic" | "transferTraffic">) => {
            state.uiState.mode = action.payload
            setChannelMode(action.payload)
        },
        removeCurrentChannel: (state: InitialStateType) => {
            state.currentChannel.data = null
        },
        editCurrentChannel: (state: InitialStateType, action: PayloadAction<IChannelResponseData>) => {
            if (state.currentChannel.data) {
                state.currentChannel.data.language = action.payload.language;
                state.currentChannel.data.timezone = action.payload.timezone;
                state.currentChannel.data.categories = action.payload.categories;
            }
        },
        editBlacklistFlagChannel: (state: InitialStateType) => {
            if (state.currentChannel.data) {
                state.currentChannel.data.blacklisted = !state.currentChannel.data.blacklisted
            }
        },
        editFavouriteFlagChannel: (state: InitialStateType) => {
            if (state.currentChannel.data) {
                state.currentChannel.data.favourite = !state.currentChannel.data.favourite
            }
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setChannelsToFavourites: (state, action: PayloadAction<SetAllChannelsDataPayload>) => {
            const {data, append} = action.payload;

            if (append && state.favourites) {
                state.favourites.data = [
                    ...state.favourites.data,
                    ...data.data,
                ];
                state.favourites.total = data.total;
                state.favourites.page = data.page;
                state.favourites.limit = data.limit;
                state.favourites.pages = data.pages;
            } else {
                state.favourites = data;
            }
        },
        setChannelsToBlackList: (state, action: PayloadAction<SetAllChannelsDataPayload>) => {
            const {data, append} = action.payload;

            if (append && state.blackList) {
                state.blackList.data = [
                    ...state.blackList.data,
                    ...data.data,
                ];
                state.blackList.total = data.total;
                state.blackList.page = data.page;
                state.blackList.limit = data.limit;
                state.blackList.pages = data.pages;
            } else {
                state.blackList = data;
            }
        },
    },
});
export const channelReducer = slice.reducer;
export const channelActions = slice.actions;

export const setInitialChannelsDataTC = (): AppThunk<Promise<IChannelResponseData[] | void>> =>
    async (dispatch) => {
        dispatch(channelActions.setLoading(true));
        try {
            const res = await ChannelService.fetchChannelsData();
            dispatch(channelActions.setInitialData(res.data))

            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(err.message, 'main', 'bottom-center');
            throw err;
        } finally {
            dispatch(channelActions.setLoading(false));
        }
    };

export const setPublicationsChannelsDataTC = (params: string[]): AppThunk<Promise<IChannelResponseData[] | void>> =>
    async (dispatch) => {
        dispatch(channelActions.setLoading(true));
        try {
            const res = await ChannelService.fetchChannelsDataWithQuery(params);
            dispatch(channelActions.setPublicationsChannelsData(res.data))

            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(err.message, 'main', 'bottom-center');
            throw err;
        } finally {
            dispatch(channelActions.setLoading(false));
        }
    };

export const setFilteredChannelsDataTC = (params: FilteredChannelParams, existQuery?: boolean): AppThunk<Promise<AvailableChannelsType | void>> =>
    async (dispatch) => {
        dispatch(channelActions.setLoading(true));
        try {
            let queryParams = params
            if (existQuery) {
                queryParams = {tp: true, ...params}
            }
            const res = await ChannelService.fetchFilteredChannelsData(queryParams);
            dispatch(channelActions.setAvailableChannelsData(res.data));
            setChannelFilters(queryParams)
            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(err.message, 'main', 'bottom-center');
            throw err;
        } finally {
            dispatch(channelActions.setLoading(false));
        }
    };

export const setEngagementFilteredChannelsDataTC = (params: FilteredChannelParams, existQuery?: boolean): AppThunk<Promise<AvailableChannelsType | void>> =>
    async (dispatch) => {
        dispatch(channelActions.setLoading(true));
        try {
            let queryParams = params
            if (existQuery) {
                queryParams = {tp: true, ...params}
            }
            const res = await ChannelService.fetchFilteredChannelsData(queryParams);
            dispatch(channelActions.setEngagementAvailableChannelsData(res.data))
            setEngagementChannelFilters(queryParams)
            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(err.message, 'main', 'bottom-center');
            throw err;
        } finally {
            dispatch(channelActions.setLoading(false));
        }
    };

export const setMyChannelsDataTC = (): AppThunk<Promise<IChannelResponseData[] | void>> =>
    async (dispatch) => {
        dispatch(channelActions.setLoading(true));
        try {
            const res = await ChannelService.fetchChannelsData();
            dispatch(channelActions.setMyChannels(res.data))
            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(err.message, 'main', 'bottom-center');
            throw err;
        } finally {
            dispatch(channelActions.setLoading(false));
        }
    };

export const toggleMyChannelTC = (id:string): AppThunk<Promise<IChannelResponseData[] | void>> =>
    async (dispatch) => {
        dispatch(channelActions.setLoading(true));
        try {
            const res = await ChannelService.fetchChannelsData();
            dispatch(channelActions.toggleMyChannelChecked(id))
            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(err.message, 'main', 'bottom-center');
            throw err;
        } finally {
            dispatch(channelActions.setLoading(false));
        }
    };

export const setFilteredAllChannelsDataTC = (
    params: FilteredChannelParams,
    append: boolean = false
): AppThunk<Promise<AvailableChannelsType | void>> =>
    async (dispatch) => {
        dispatch(channelActions.setLoading(true));
        try {
            const res = await ChannelService.fetchFilteredChannelsData(params);
            dispatch(channelActions.setAllChannelsData({
                data: res.data,
                append
            }));
            dispatch(channelActions.setParamsOfAllChannelsData(params));
            setParamsOfAllChannels(params);
            return res.data;
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(t('pages.allChannels.state.errorResponse'), 'main', 'bottom-center');
            throw err;
        } finally {
            dispatch(channelActions.setLoading(false));
        }
    };

export const setAllChannelsDataTC = (
    params: FilteredChannelParams,
    append: boolean = false
): AppThunk<Promise<AvailableChannelsType | void>> =>
    async (dispatch) => {
        dispatch(channelActions.setLoading(true));

        try {
            const res = await ChannelService.fetchFilteredChannelsData(params);
            dispatch(channelActions.setAllChannelsData({
                data: res.data,
                append
            }));
            dispatch(channelActions.setParamsOfAllChannelsData(params));
            setParamsOfAllChannels(Object.fromEntries(
                Object.entries(params).filter(([key]) => key !== 'page')
            ));
            return res.data;
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(t('pages.allChannels.state.errorResponse'), 'main', 'bottom-center');
            throw err;
        } finally {
            dispatch(channelActions.setLoading(false));
        }
    };

export const createNewChannelTC = (params: ICreateChannelParams): AppThunk<Promise<{
    data: IChannelResponseData
} | void>> =>
    async (dispatch) => {
        dispatch(channelActions.setLoading(true));
        try {
            const res = await ChannelService.createNewChannel(params);
            dispatch(channelActions.setNewChannel(res.data))
            setCurrentChannelId(res.data.id)
            dispatch(channelActions.setLoading(false));
            return res
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(t('pages.addChannelPage.state.errorResponse'), 'main', 'bottom-center')
            throw err;
        }
    };

export const editChannelTC = (id: string, params: IEditChannelParams): AppThunk<Promise<{
    data: IChannelResponseData
} | void>> =>
    async (dispatch) => {
        try {
            const res = await ChannelService.editChannel(id, params);
            dispatch(channelActions.editCurrentChannel(res.data));
            successToastify(t('pages.editChannel.successResponse'), 'bottom-center', 'main');
            return res
        } catch (err) {
            if (err instanceof AxiosError)
                console.log(err)
            showCustomErrorToast(t('pages.editChannel.errorResponse'), 'main', 'bottom-center')
            throw err;
        }
    };

export const checkTelegramUsernameTC = (channel_username: string): AppThunk<Promise<{
    data: IPrecheckChannelData
} | void>> =>
    async (dispatch) => {
        channelActions.setPrecheckChannelDataData(null)
        try {
            const res = await ChannelService.checkTelegramUsername(channel_username);
            dispatch(channelActions.setPrecheckChannelDataData(res.data));
            successToastify(t('hooks.searchChannel.state.successResponse'), 'bottom-center', 'main');
            return res
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(t('hooks.searchChannel.state.errorResponse'), 'main', 'bottom-center')
            throw err;
        }
    };

export const setCurrentChannelTC = (id: string): AppThunk<Promise<{ data: IChannelResponseData } | void>> =>
    async (dispatch) => {
        dispatch(channelActions.setIsLoadingCurrentChannel(true));
        dispatch(channelActions.setCurrentChannel(null))
        try {
            const res = await ChannelService.getCurrentChannel(id);
            dispatch(channelActions.setCurrentChannel(res.data))
            dispatch(channelActions.setIsLoadingCurrentChannel(false));
            return res
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(t('common.setCurrentChannel.errorResponse'), 'main', 'bottom-center')
            throw err;
        } finally {
            dispatch(channelActions.setLoading(false));
        }
    };

export const setUpCurrentChannelTC = (id: string): AppThunk<Promise<{ data: IChannelResponseData } | void>> =>
    async (dispatch) => {
        dispatch(channelActions.setIsLoadingCurrentChannel(true));
        try {
            const res = await ChannelService.getCurrentChannel(id);
            dispatch(channelActions.setCurrentChannel(res.data))
            dispatch(channelActions.setIsLoadingCurrentChannel(false));
            return res
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(t('common.setCurrentChannel.errorResponse'), 'main', 'bottom-center')
            throw err;
        } finally {
            dispatch(channelActions.setLoading(false));
        }
    };

export const setCurrentPublicationChannelTC = (id: string): AppThunk<Promise<{ data: IChannelResponseData } | void>> =>
    async (dispatch) => {
        dispatch(channelActions.setIsLoadingPublicationCurrentChannel(true));
        try {
            const res = await ChannelService.getCurrentChannel(id);
            dispatch(channelActions.setCurrentPublicationChannel(res.data))
            dispatch(channelActions.setIsLoadingPublicationCurrentChannel(false));
            return res
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(t('common.setCurrentChannel.errorResponse'), 'main', 'bottom-center')
            throw err;
        } finally {
            dispatch(channelActions.setIsLoadingPublicationCurrentChannel(false));
        }
    };

export const deleteChannelTC = (id: string): AppThunk<Promise<IChannelResponseData | void>> =>
    async (dispatch) => {
        try {
            const res = await ChannelService.deleteChannel(id);
            dispatch(channelActions.removeCurrentChannel())
            removeCurrentChannelId()
            successToastify(t('modals.deleteChannel.successResponse'), 'bottom-center', 'main');
            return res
        } catch (err) {
            if (err instanceof AxiosError) {
                showCustomErrorToast(t('modals.deleteChannel.errorResponse'), 'main', 'bottom-center')

                throw err;
            }
        }
    };

export const setChannelToBlackListTC = (data: IUserBlackListParams): AppThunk<Promise<string | void>> =>
    async (dispatch) => {
        try {
            const res = await ChannelService.setChannelToBlackList(data);
            dispatch(channelActions.editBlacklistFlagChannel());
            successToastify(t('hooks.setChannelToBlacklist.successResponse'), 'top-center', 'publication_details_id');

            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                if (err.response?.status === 400) {
                    showCustomErrorToast(t('hooks.setChannelToBlacklist.errorResponse'), 'publication_details_id', 'top-center')
                }
        }
    };

export const removeChannelFromBlackListTC = (channelId: string): AppThunk<Promise<string | void>> =>
    async (dispatch) => {
        try {
            const res = await ChannelService.removeChannelFromBlackList(channelId);
            dispatch(channelActions.editBlacklistFlagChannel());
            successToastify(t('hooks.removeChannelFromBlacklist.successResponse'), 'top-center', 'publication_details_id');

            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(t('hooks.removeChannelFromBlacklist.errorResponse'), 'publication_details_id', 'top-center')
        }
    };

export const setChannelToFavouritesTC = (data: IUserBlackListParams): AppThunk<Promise<string | void>> =>
    async (dispatch) => {
        try {
            const res = await ChannelService.setChannelToFavourites(data);
            dispatch(channelActions.editFavouriteFlagChannel());
            successToastify(t('hooks.setChannelToFavourites.successResponse'), 'top-center', 'publication_details_id');

            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(t('hooks.setChannelToFavourites.errorResponse'), 'publication_details_id', 'top-center')
        }
    };

export const removeChannelFromFavouritesTC = (channelId: string): AppThunk<Promise<string | void>> =>
    async (dispatch) => {
        try {
            const res = await ChannelService.removeChannelFromFavourites(channelId);
            dispatch(channelActions.editFavouriteFlagChannel());
            successToastify(t('hooks.removeChannelFromFavourites.successResponse'), 'top-center', 'publication_details_id');

            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(t('hooks.removeChannelFromFavourites.errorResponse'), 'publication_details_id', 'top-center')
        }
    };

export const fetchBlackListChannelsTC = (params: FilteredChannelParams, append: boolean = false): AppThunk<Promise<string | void>> =>
    async (dispatch) => {
        dispatch(channelActions.setLoading(true));
        try {
            const res = await ChannelService.getBlackListChannel(params);
            dispatch(channelActions.setChannelsToBlackList({
                data: res.data,
                append
            }))
            if (Object.keys(params).length !== 0) {
                setParamsBlackListChannels(Object.fromEntries(
                    Object.entries(params).filter(([key]) => key !== 'page')
                ))
            } else {
                setParamsBlackListChannels({})
            }
            dispatch(channelActions.setLoading(false));
            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(t('pages.blackListPage.state.errorResponse'), 'main', 'bottom-center')
        } finally {
            dispatch(channelActions.setLoading(false));
        }
    };

export const fetchFavouritesChannelsTC = (params: FilteredChannelParams, append: boolean = false): AppThunk<Promise<string | void>> =>
    async (dispatch) => {
        dispatch(channelActions.setLoading(true));
        try {
            const res = await ChannelService.getFavouritesChannels(params);
            dispatch(channelActions.setChannelsToFavourites({
                data: res.data,
                append
            }))
            if (Object.keys(params).length !== 0) {
                setParamsFavouritesChannels(Object.fromEntries(
                    Object.entries(params).filter(([key]) => key !== 'page')
                ));
            } else {
                setParamsFavouritesChannels({})
            }
            dispatch(channelActions.setLoading(false));
            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(t('pages.favouritesPage.state.errorResponse'), 'main', 'bottom-center')

        }
    };